import React, { useState, useEffect, useRef } from "react";
import {
  X,
  Send,
  UploadCloud,
  FileText,
  ChevronDown,
} from "lucide-react";
import MarkdownEditor from "./MarkdownEditor";
import { processFileForAnnouncement } from "./fileUploadHelper";
import { toast } from "react-toastify";

const SCOPE_OPTIONS = [
  {
    value: "all",
    scope: "all_users",
    label: "All Users",
    description: "Broadcast to all companies and employees",
  },
  {
    value: "all_companies",
    scope: "all_company",
    label: "All Companies",
    description: "Broadcast to company administrators only",
  },
  {
    value: "all_employees",
    scope: "all_employee",
    label: "All Employees",
    description: "Broadcast to all employees across registered companies",
  },
];

export default function SendAnnouncementModal({
  isOpen,
  onClose,
  onSave,
  initialData = null,
}) {
  const [formData, setFormData] = useState({
    title: "",
    message: "",
    targetType: "all",
  });
  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(false);

  // File attachment state
  const [attachedFile, setAttachedFile] = useState(null);
  const [imagePreview, setImagePreview] = useState(null);
  const fileInputRef = useRef(null);

  useEffect(() => {
    if (isOpen) {
      if (initialData) {
        setFormData({
          title: initialData.title || "",
          message: initialData.message || initialData.text || "",
          targetType: initialData.scope === "all_company"
            ? "all_companies"
            : initialData.scope === "all_employee"
            ? "all_employees"
            : "all",
        });
        setImagePreview(initialData.image || null);
        setAttachedFile(initialData.attachment || null);
      } else {
        setFormData({
          title: "",
          message: "",
          targetType: "all",
        });
        setImagePreview(null);
        setAttachedFile(null);
      }
      setErrors({});
    }
  }, [isOpen, initialData]);

  if (!isOpen) return null;

  const handleTitleChange = (e) => {
    const val = e.target.value;
    if (val.length <= 250) {
      setFormData((prev) => ({ ...prev, title: val }));
      if (errors.title) setErrors((prev) => ({ ...prev, title: "" }));
    }
  };

  const handleMessageChange = (newMarkdown) => {
    setFormData((prev) => ({ ...prev, message: newMarkdown }));
    if (errors.message) setErrors((prev) => ({ ...prev, message: "" }));
  };

  const handleFileSelect = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    try {
      const processed = await processFileForAnnouncement(file);
      if (processed.category === "IMAGE") {
        const reader = new FileReader();
        reader.onload = () => {
          setImagePreview(reader.result);
          setAttachedFile({
            file: processed.file,
            name: processed.name,
            size: processed.size,
            type: "IMAGE",
            dataUrl: reader.result,
          });
        };
        reader.readAsDataURL(processed.file);
      } else {
        const reader = new FileReader();
        reader.onload = () => {
          setImagePreview(null);
          setAttachedFile({
            file: processed.file,
            name: processed.name,
            size: processed.size,
            type: processed.category,
            dataUrl: reader.result,
          });
        };
        reader.readAsDataURL(processed.file);
      }
    } catch (err) {
      toast.error(err.message || "Failed to process file");
    } finally {
      if (fileInputRef.current) fileInputRef.current.value = "";
    }
  };

  const handleRemoveAttachment = () => {
    setAttachedFile(null);
    setImagePreview(null);
    if (fileInputRef.current) fileInputRef.current.value = "";
  };

  const validate = () => {
    const newErrors = {};
    if (!formData.title.trim()) {
      newErrors.title = "Announcement title is required";
    }
    const cleanMsg = (formData.message || "").replace(/<[^>]*>?/gm, "").trim();
    if (!cleanMsg && !formData.message.trim()) {
      newErrors.message = "Announcement message is required";
    }
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validate()) return;

    setLoading(true);
    try {
      const selectedScopeObj = SCOPE_OPTIONS.find(
        (opt) => opt.value === formData.targetType
      );
      const scope = selectedScopeObj ? selectedScopeObj.scope : "all_users";

      const announcementPayload = {
        id: initialData ? (initialData._id || initialData.id) : undefined,
        title: formData.title.trim(),
        message: formData.message.trim(),
        targetType: formData.targetType,
        scope,
        type: formData.type || "info",
        rawFile: attachedFile?.file || null,
        image: imagePreview,
        attachment: attachedFile
          ? {
              name: attachedFile.name,
              size: attachedFile.size,
              type: attachedFile.type,
              url: attachedFile.dataUrl || attachedFile.url,
            }
          : null,
      };

      await onSave(announcementPayload);
    } catch (err) {
      toast.error(err.message || (initialData ? "Failed to update announcement" : "Failed to send announcement"));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-3 sm:p-4 bg-black/50 backdrop-blur-xs overflow-y-auto">
      <div className="relative w-full max-w-2xl bg-white dark:bg-gray-800 rounded-2xl shadow-2xl border border-gray-100 dark:border-gray-700 max-h-[92vh] flex flex-col my-auto animate-fadeIn">
        {/* Modal Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-100 dark:border-gray-700 shrink-0">
          <h2 className="text-lg sm:text-xl font-bold text-gray-900 dark:text-white">
            {initialData ? "Edit Announcement" : "Send Announcement"}
          </h2>
          <button
            type="button"
            onClick={onClose}
            className="p-1.5 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Modal Body */}
        <form onSubmit={handleSubmit} className="p-6 space-y-5 overflow-y-auto flex-1">
          {/* Title */}
          <div>
            <label className="block text-sm font-semibold text-gray-900 dark:text-white mb-1.5">
              Title <span className="text-red-500">*</span>
            </label>
            <input
              type="text"
              value={formData.title}
              onChange={handleTitleChange}
              placeholder="Enter announcement title"
              className={`w-full px-4 py-2.5 text-sm sm:text-base border rounded-xl outline-none focus:outline-none transition-all bg-white dark:bg-gray-800 dark:text-white placeholder-gray-400 ${
                errors.title
                  ? "border-red-500 focus:ring-2 focus:ring-red-200 dark:focus:ring-red-900/40"
                  : "border-gray-200 dark:border-gray-700 focus:border-gray-300 dark:focus:border-gray-600 focus:ring-0"
              }`}
            />
            <div className="flex items-center justify-between mt-1 px-1">
              {errors.title ? (
                <p className="text-xs text-red-500 font-medium">{errors.title}</p>
              ) : (
                <span />
              )}
              <span className="text-xs text-gray-400 dark:text-gray-500">
                {formData.title.length}/250 characters
              </span>
            </div>
          </div>

          {/* Message (In-Place WYSIWYG Markdown Editor) */}
          <div>
            <label className="block text-sm font-semibold text-gray-900 dark:text-white mb-1.5">
              Message <span className="text-red-500">*</span>
            </label>
            <MarkdownEditor
              value={formData.message}
              onChange={handleMessageChange}
              placeholder="Enter your announcement message..."
              height={160}
              className={
                errors.message
                  ? "!border-red-500 ring-2 ring-red-200 dark:ring-red-900/40"
                  : ""
              }
            />
            {errors.message && (
              <p className="text-xs text-red-500 font-medium mt-1 px-1">
                {errors.message}
              </p>
            )}
          </div>

          {/* Announcement Cover Image / Attachment */}
          <div>
            <label className="block text-sm font-semibold text-gray-900 dark:text-white mb-1.5">
              Announcement Cover Image / Attachment{" "}
              <span className="text-gray-400 font-normal">(Optional)</span>
            </label>

            {/* Hidden native input */}
            <input
              ref={fileInputRef}
              type="file"
              accept="image/jpeg,image/png,image/webp,image/gif,application/pdf,.doc,.docx"
              onChange={handleFileSelect}
              className="hidden"
            />

            {imagePreview ? (
              /* Image Preview Box */
              <div className="relative rounded-xl border border-gray-200 dark:border-gray-700 overflow-hidden bg-gray-50 dark:bg-gray-900 group">
                <img
                  src={imagePreview}
                  alt="Attachment Preview"
                  className="w-full max-h-56 object-contain mx-auto p-2"
                />
                <button
                  type="button"
                  onClick={handleRemoveAttachment}
                  className="absolute top-2 right-2 p-1.5 bg-black/60 hover:bg-black/80 text-white rounded-full transition-all shadow-md"
                  title="Remove image"
                >
                  <X className="w-4 h-4" />
                </button>
                <div className="px-3 py-1.5 bg-white/90 dark:bg-gray-800/90 text-xs text-gray-600 dark:text-gray-300 border-t border-gray-100 dark:border-gray-700 flex justify-between items-center">
                  <span className="truncate">{attachedFile?.name || "Uploaded image"}</span>
                  <span className="shrink-0 font-medium">{attachedFile?.size}</span>
                </div>
              </div>
            ) : attachedFile ? (
              /* Document Preview Box */
              <div className="flex items-center justify-between p-3.5 rounded-xl border border-teal-200 dark:border-teal-900/60 bg-teal-50/40 dark:bg-teal-950/20">
                <div className="flex items-center gap-3 min-w-0">
                  <div className="w-10 h-10 rounded-lg bg-teal-600 text-white flex items-center justify-center font-bold text-xs shrink-0">
                    <FileText className="w-5 h-5" />
                  </div>
                  <div className="min-w-0">
                    <p className="text-sm font-semibold text-gray-900 dark:text-white truncate">
                      {attachedFile.name}
                    </p>
                    <p className="text-xs text-gray-500 dark:text-gray-400">
                      {attachedFile.size} · {attachedFile.type}
                    </p>
                  </div>
                </div>
                <button
                  type="button"
                  onClick={handleRemoveAttachment}
                  className="p-1.5 text-gray-400 hover:text-red-500 dark:hover:text-red-400 rounded-lg transition-colors shrink-0"
                >
                  <X className="w-5 h-5" />
                </button>
              </div>
            ) : (
              /* Dropzone Prompt matching mockup */
              <div
                onClick={() => fileInputRef.current?.click()}
                className="border-2 border-dashed border-gray-200 dark:border-gray-700 hover:border-[#05DAC3] dark:hover:border-[#05DAC3] rounded-2xl p-6 text-center cursor-pointer transition-all hover:bg-teal-50/20 dark:hover:bg-teal-950/10 group"
              >
                <div className="w-12 h-12 mx-auto mb-3 rounded-full bg-teal-50 dark:bg-teal-900/30 text-[#00B4A6] dark:text-[#05DAC3] flex items-center justify-center group-hover:scale-105 transition-transform">
                  <UploadCloud className="w-6 h-6" />
                </div>
                <p className="text-sm font-semibold text-gray-900 dark:text-white mb-1">
                  Click to upload cover image, PDF, or Word document
                </p>
                <p className="text-xs text-gray-500 dark:text-gray-400">
                  Image: JPEG/PNG/WEBP/GIF up to 5MB · Document: PDF/DOC/DOCX up to 10MB
                </p>
              </div>
            )}
          </div>

          {/* Send To */}
          <div>
            <label className="block text-sm font-semibold text-gray-900 dark:text-white mb-1.5">
              Send To <span className="text-red-500">*</span>
            </label>
            <div className="relative">
              <select
                value={formData.targetType}
                onChange={(e) =>
                  setFormData((prev) => ({ ...prev, targetType: e.target.value }))
                }
                className="w-full px-4 py-2.5 text-sm sm:text-base border border-gray-200 dark:border-gray-700 rounded-xl outline-none focus:outline-none appearance-none bg-white dark:bg-gray-800 dark:text-white focus:border-gray-300 dark:focus:border-gray-600 focus:ring-0 cursor-pointer pr-10"
              >
                {SCOPE_OPTIONS.map((opt) => (
                  <option key={opt.value} value={opt.value} className="py-2">
                    {opt.label} ({opt.description})
                  </option>
                ))}
              </select>
              <div className="absolute inset-y-0 right-0 flex items-center px-3.5 pointer-events-none text-gray-400">
                <ChevronDown className="w-4 h-4" />
              </div>
            </div>
          </div>

          {/* Action Buttons */}
          <div className="flex items-center justify-end gap-3 pt-4 border-t border-gray-100 dark:border-gray-700 shrink-0">
            <button
              type="button"
              onClick={onClose}
              disabled={loading}
              className="px-5 py-2.5 text-sm font-medium text-gray-700 dark:text-gray-300 bg-gray-100 hover:bg-gray-200 dark:bg-gray-700 dark:hover:bg-gray-600 rounded-xl transition-colors disabled:opacity-50"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={loading}
              className="inline-flex items-center gap-2 px-5 py-2.5 text-sm font-semibold text-white bg-[#05DAC3] hover:bg-[#00B4A6] rounded-xl transition-all shadow-sm hover:shadow-md disabled:opacity-50"
            >
              <Send className="w-4 h-4" />
              <span>
                {loading
                  ? initialData
                    ? "Updating..."
                    : "Sending..."
                  : initialData
                  ? "Update Announcement"
                  : "Send Announcement"}
              </span>
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
